<script lang="ts">
  import type { AproposVideoProps } from './types';
  import { onDestroy, onMount } from 'svelte';

  export let entryId: AproposVideoProps['entryId'];
  export let sourceConfigs: AproposVideoProps['sourceConfigs'];
  export let loop: AproposVideoProps['loop'];
  export let reset: AproposVideoProps['reset'];
  export let autoplay: AproposVideoProps['autoplay'];
  export let playOnPageLoad: AproposVideoProps['playOnPageLoad'];
  export let customCssClass: AproposVideoProps['customCssClass'];
  export let fallbackImageUrl: AproposVideoProps['fallbackImageUrl'] = undefined;
  export let aboveTheFold: AproposVideoProps['aboveTheFold'] = false;

  const INTERSECTION_THRESHOLD = 0.99;

  type VideoState = 'playing' | 'paused';

  let video: HTMLVideoElement;
  let observer: IntersectionObserver | null = null;
  let prefersReducedMotion = false;

  // Used to track video's intended play state before accessibility settings come into effect
  let desiredVideoState: VideoState = 'paused';

  const validateProps = () => {
    sourceConfigs.forEach((source) => {
      if (!source.src) {
        console.error(`Video Component: ${entryId} has an asset with a missing file`);
      }
    });
  };

  const playVideo = () => {
    if (!prefersReducedMotion) {
      video.play();
    }
  };

  const pauseVideo = () => {
    video.pause();

    if (!loop && reset) {
      // not looping and reset is set so reset playback
      video.currentTime = 0;
    }
  };

  function onIntersectionChange(entry: IntersectionObserverEntry) {
    if (entry.isIntersecting) {
      // video is at least partially visible
      if (loop) {
        // video is looping and should play
        desiredVideoState = 'playing';
        playVideo();
      } else if (entry.intersectionRatio >= INTERSECTION_THRESHOLD) {
        // video is fully visible
        if (video.currentTime !== video.duration || reset) {
          // playback hasn't finished or reset is true, so play
          desiredVideoState = 'playing';
          playVideo();
        }
      }
    } else {
      // video is offscreen
      desiredVideoState = 'paused';
      pauseVideo();
    }
  }

  const setupVideo = () => {
    if (loop) {
      video.loop = true;
    }

    if (autoplay !== false) {
      observer = new IntersectionObserver((entries) => onIntersectionChange(entries[0]), {
        threshold: [0, INTERSECTION_THRESHOLD],
      });
      observer.observe(video);
    }
  };

  // Respond to change in prefersReduceMotion
  // If video is playing and user enables prefersReducedMotion, it pauses the video
  // If video is expected to play and user disables prefersReducedMotion, it plays the video
  const onPrefersReducedMotionChange = () => {
    if (prefersReducedMotion && !video.paused) {
      pauseVideo();
    } else if (!prefersReducedMotion && desiredVideoState === 'playing' && video.paused) {
      playVideo();
    }
  };

  const setupPrefersReducedMotion = () => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion = mediaQuery.matches;
    mediaQuery.addEventListener('change', () => {
      prefersReducedMotion = mediaQuery.matches;
      onPrefersReducedMotionChange();
    });
  };

  validateProps();

  onMount(() => {
    setupPrefersReducedMotion();
    if (video) {
      if (playOnPageLoad) {
        desiredVideoState = 'playing';
        onPrefersReducedMotionChange();
      }

      setupVideo();
    }
  });

  onDestroy(() => {
    video?.pause();
    observer?.disconnect();
  });
</script>

<svelte:head>
  {#if aboveTheFold && fallbackImageUrl !== undefined}
    <link rel="preload" href={fallbackImageUrl} as="image" data-entry-id={entryId} />
  {/if}
</svelte:head>

<div class="AproposVideo {customCssClass}" data-entry-id={entryId}>
  {#if sourceConfigs.length > 0}
    <video
      bind:this={video}
      playsinline
      autoplay={playOnPageLoad}
      muted
      data-id={entryId}
      poster={fallbackImageUrl}
      preload="metadata"
    >
      {#each sourceConfigs as config}
        {#if config.src}
          <source src={config.src} media={config.media} type={config.type} />
        {/if}
      {/each}
    </video>
  {/if}
</div>

<!--Copied from VideoComponent.svelte -->
<style lang="scss">
  .AproposVideo {
    overflow: hidden;

    video {
      display: block;
      object-position: center center;
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }
</style>
